import {Box, Button, Chip, Divider, Stack, TextField, Typography} from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {useEffect, useState} from "react";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {useMutation, useQuery} from "@tanstack/react-query";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import {ArrowBack, Edit} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router-dom";
import {format} from "date-fns";
import {Close} from "@mui/icons-material";
import {
	Dialog,
	DialogActions,
	DialogTitle,
	DialogContent,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper
} from "@mui/material";
import {useSnackbar} from "notistack";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface PageData {
	status_code: number
	status_text: string

	user_email: string
	workspaces: Workspace[]
}

interface Domain {
	id: number
	subdomain: string
	status: string
	warmup_percent: number
	warmup_days_remaining: number
}

interface Workspace {
	id: number
	name: string
	created_on_ts: number
	total_emails_sent: number
	total_replies_received: number
	plan_domain_limit: number
	domain_limit_override: number
	domain_limit: number
	total_domains_connected: number
	current_plan: string
	total_campaigns: number
	domains: Domain[]
}

interface PaginationData {
	data: Workspace[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
	user_email: string;
}

export default function AdminUserWorkspaces() {
	const {userId} = useParams();
	const { user } = useAuth();
	console.log("user ------------- ", user)
	const navigate = useNavigate();
	const {enqueueSnackbar} = useSnackbar();
	
	const [pageData, setPageData] = useState<PaginationData>();
	const [domainModalOpen, setDomainModalOpen] = useState(false);
	const [selectedDomains, setSelectedDomains] = useState<Domain[]>([]);
	const [selectedWorkspaceName, setSelectedWorkspaceName] = useState("");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [domainLimitDialogOpen, setDomainLimitDialogOpen] = useState(false);
	const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
	const [domainLimitOverride, setDomainLimitOverride] = useState("");
	const [exportLoading, setExportLoading] = useState(false);

	// Query to fetch all workspaces.
	const adminUserWorkspacesQuery = useQuery({
		queryKey: [
			"adminUserWorkspacesQuery",
			userId,
			page,
			pageSize,
			search
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/get-user-workspaces/?user_id=${userId}&page=${page}&page_size=${pageSize}&search=${search}`
			),
		gcTime: 0,
		refetchOnWindowFocus: false,
		retry: retryFn,
	});

	const updateDomainLimitOverrideMutation = useMutation({
		mutationKey: ["updateDomainLimitOverride"],
		mutationFn: (data: {workspace_id: number; domain_limit_override: number}) =>
			authenticateAndPostData("/admin/update-workspace-domain-limit-override/", data),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (response) => {
			const updatedWorkspace = response.data;
			setPageData((currentPageData) => currentPageData ? {
				...currentPageData,
				data: currentPageData.data.map((workspace) => workspace.id === updatedWorkspace.workspace_id ? {
					...workspace,
					plan_domain_limit: updatedWorkspace.plan_domain_limit,
					domain_limit_override: updatedWorkspace.domain_limit_override,
					domain_limit: updatedWorkspace.domain_limit,
				} : workspace),
			} : currentPageData);
			enqueueSnackbar("Domain limit override updated successfully.", {variant: "success"});
			setDomainLimitDialogOpen(false);
			setSelectedWorkspace(null);
			setDomainLimitOverride("");
		},
		onError: (error: ApiRequestFailed) => {
			enqueueSnackbar(error.data.message, {variant: "error"});
		},
	});

	useEffect(() => {
		if (adminUserWorkspacesQuery.data?.data) {
			const response = adminUserWorkspacesQuery.data.data;

			setPageData({
				data: response.data || [],
				total_count: response.total_count,
				total_pages: response.total_pages,
				current_page: response.current_page,
				page_size: response.page_size,
				user_email: response.user_email,
			});
		}
	}, [adminUserWorkspacesQuery.data]);

	function timestampFormatter(params: ValueFormatterParams) {
		return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
	}

	const handleOpenDomainDialog = (workspace: Workspace) => {
		setSelectedDomains(workspace.domains || []);
		setSelectedWorkspaceName(workspace.name);
		setDomainModalOpen(true);
	};

	const handleCloseDomainDialog = () => {
		setDomainModalOpen(false);
		setSelectedDomains([]);
		setSelectedWorkspaceName("");
	};	

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	const handleOpenDomainLimitDialog = (workspace: Workspace) => {
		setSelectedWorkspace(workspace);
		setDomainLimitOverride(workspace.domain_limit_override.toString());
		setDomainLimitDialogOpen(true);
	};

	const handleCloseDomainLimitDialog = () => {
		if (updateDomainLimitOverrideMutation.isPending) return;
		setDomainLimitDialogOpen(false);
		setSelectedWorkspace(null);
		setDomainLimitOverride("");
	};

	const domainLimitOverrideIsValid = /^\d+$/.test(domainLimitOverride.trim());
	const parsedDomainLimitOverride = domainLimitOverrideIsValid ? Number(domainLimitOverride) : 0;
	const effectiveDomainLimit = selectedWorkspace
		? selectedWorkspace.plan_domain_limit + parsedDomainLimitOverride
		: 0;

	const handleSaveDomainLimitOverride = () => {
		if (!selectedWorkspace || !domainLimitOverrideIsValid) return;
		updateDomainLimitOverrideMutation.mutate({
			workspace_id: selectedWorkspace.id,
			domain_limit_override: parsedDomainLimitOverride,
		});
	};

	const DomainsDialog = () => {
    if (!selectedWorkspaceName) return null;

		return (
			<Dialog
				open={domainModalOpen}
				onClose={() => setDomainModalOpen(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">
							Domains for {selectedWorkspaceName}
						</Typography>

						<IconButton
							onClick={() => setDomainModalOpen(false)}
							size="small"
						>
							<Close />
						</IconButton>
					</Box>
				</DialogTitle>

				<DialogContent>
					{selectedDomains && selectedDomains.length > 0 ? (
						<TableContainer
							component={Paper}
							variant="outlined"
							sx={{
								maxHeight: 500,
								overflowY: "auto"
							}}
						>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										<TableCell>
											<strong>Domain</strong>
										</TableCell>
										<TableCell>
											<strong>Status</strong>
										</TableCell>
										<TableCell>
											<strong>Warmup %</strong>
										</TableCell>
										<TableCell>
											<strong>Days Remaining</strong>
										</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{selectedDomains.map((domain) => (
										<TableRow key={domain.id}>
											<TableCell>
												{domain.subdomain}
											</TableCell>

											<TableCell>
												<Chip
													label={domain.status}
													color={
														domain.status === "active"
															? "success"
															: domain.status === "warmup"
															? "warning"
															: "default"
													}
													size="small"
												/>
											</TableCell>

											<TableCell>
												{domain.warmup_percent}%
											</TableCell>

											<TableCell>
												{domain.warmup_days_remaining}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<Typography
							variant="body2"
							sx={{
								fontStyle: "italic",
								color: "text.secondary"
							}}
						>
							No domains available
						</Typography>
					)}
				</DialogContent>
			</Dialog>
		);
	};

	const domainLimitDialog = selectedWorkspace ? (
		<Dialog open={domainLimitDialogOpen} onClose={handleCloseDomainLimitDialog} maxWidth="sm" fullWidth>
			<DialogTitle>Edit Domain Limit Override</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{pt: 1}}>
					<Typography variant="body2" color="text.secondary">
						{selectedWorkspace.name} has a plan limit of {selectedWorkspace.plan_domain_limit} domains.
					</Typography>
					<TextField
						label="Additional Domains"
						type="number"
						value={domainLimitOverride}
						onChange={(event) => setDomainLimitOverride(event.target.value)}
						error={domainLimitOverride.length > 0 && !domainLimitOverrideIsValid}
						helperText={domainLimitOverride.length > 0 && !domainLimitOverrideIsValid
							? "Enter a non-negative whole number."
							: `Effective domain limit: ${effectiveDomainLimit}`}
						inputProps={{min: 0, step: 1}}
						fullWidth
						autoFocus
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCloseDomainLimitDialog} disabled={updateDomainLimitOverrideMutation.isPending}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSaveDomainLimitOverride}
					disabled={!domainLimitOverrideIsValid || updateDomainLimitOverrideMutation.isPending}
				>
					{updateDomainLimitOverrideMutation.isPending ? "Saving..." : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	) : null;


	const columnCustomizerLabel = {
		name: "Workspace Name",
		username: "Workspace ID",	
		total_domains: "Total Domains",
		created_on_ts: "Created On",
		total_emails_sent: "Total Emails Sent",
		total_replies_received: "Total Replies Received",
		total_campaigns: "Total Campaigns",
		current_plan: "Plan",
		plan_domain_limit: "Plan Domain Limit",
		domain_limit_override: "Domain Limit Override"
	};


	// Columns for workspaces table.
	const columnDefs: ColDef[] = [
		{field: "name", headerName: "Workspace Name"},
		{field: "id", headerName: "Workspace ID"},
		{
			field: "total_domains_connected",
			headerName: "Total Domains",
			valueGetter: (params) =>
				`${params.data.total_domains_connected}/${params.data.domain_limit} Domains`,
			cellRenderer: (params: any) => (
				<span
					onClick={() => handleOpenDomainDialog(params.data)}
					style={{
						cursor: "pointer",
						color: "inherit",
					}}
				>
					{params.data.total_domains_connected}/{params.data.domain_limit} Domains
				</span>
			)
		},
		{field: "created_on_ts", headerName: "Created On", valueFormatter: timestampFormatter},
		{field: "total_emails_sent", headerName: "Total Emails Sent"},
		{field: "total_campaigns", headerName: "Total Campaigns"},
		{field: "total_replies_received", headerName: "Total Replies Received", resizable: false},
		{field: "current_plan", headerName: "Plan"},
		{field: "plan_domain_limit", headerName: "Plan Domain Limit"},
		{
			field: "domain_limit_override",
			headerName: "Domain Limit Override",
			cellRenderer: (params: any) => (
				<Button
					size="small"
					variant="text"
					startIcon={<Edit/>}
					onClick={() => handleOpenDomainLimitDialog(params.data)}
				>
					+{params.data.domain_limit_override}
				</Button>
			)
		},
	]

	const workspaceColumns: CSVColumn<any>[] = [
		{ header: "Workspace Name", accessor: "name" },
		{ header: "Current Plan", accessor: "current_plan" },
		{ header: "Total Emails Sent", accessor: "total_emails_sent" },
		{ header: "Total Replies", accessor: "total_replies_received" },
		{ header: "Domains Connected", accessor: "total_domains_connected" },
		{ header: "Domain Limit", accessor: "domain_limit" },
		{ header: "Total Campaigns", accessor: "total_campaigns" },
	];

	const ExportUserWorkspacesCsvButton = () => {		
		const handleExportWorkspaces = async () => {
			setExportLoading(true);

			try {
				const response = await authenticateAndFetchData(
					`/admin/get-user-workspaces/?user_id=${userId}&export_all=true`
				);
				
				const workspacesToExport = response?.data?.data || [];

				if (!workspacesToExport.length) {
					enqueueSnackbar("No workspace data available to export.", { variant: "error" });
					return;
				}

				const success = exportToCSV(
					workspacesToExport,
					workspaceColumns,
					getExportFilename(user?.email ?? "unknown", "Workspaces")
				);

				if (success) {
					enqueueSnackbar("Workspaces exported successfully!", { variant: "success" });
				} else {
					enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
				}
			} catch (err) {
				console.error("Export error:", err);
				enqueueSnackbar("Error while exporting workspaces.", { variant: "error" });
			} finally {
				setExportLoading(false);
			}
		};

		return (
			<Button
				startIcon={<Downloading />}
				variant="contained"
				color="primary"
				onClick={handleExportWorkspaces}
				disabled={exportLoading}
			>
				{exportLoading ? "Exporting..." : "Export"}
			</Button>
		);
	};

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (!pageData && adminUserWorkspacesQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (adminUserWorkspacesQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={adminUserWorkspacesQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
				<Stack direction={"row"} spacing={2} alignItems={"center"}>
					<IconButton onClick={() => navigate(-1)}><ArrowBack/></IconButton>
					<Typography fontWeight={"bold"} variant={"h5"}>Workspaces ({pageData.user_email})</Typography>
				</Stack>
				<Divider/>
				<Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
					<PaginatedServerSideDataTable
						columns={columnDefs}
						paginationData={pageData}
						loading={adminUserWorkspacesQuery.isFetching}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						onSearchChange={handleSearchChange}
						searchPlaceholder="Search workspace name or plan..."
						columnLabels={columnCustomizerLabel}
						actions={<ExportUserWorkspacesCsvButton />}
					/>
				</Box>
				<DomainsDialog />
				{domainLimitDialog}
			</Stack>
		)
	} else {
		// Ideally it should not reach here.
		return <></>
	}
}
